import { useCallback } from "react";
import { useModal } from "src/hooks";
import type { ItemControl } from "src/hooks/useSubpages";

import useMaybeSubmit from "./useMaybeSubmit";

interface PartialSubmitForOverviewOptions<T extends object> {
  value?: ItemControl<T>;
  onNext: () => void;
  shouldSubmit?: boolean;
}

/**
 * A specific variant of `usePartialSubmit` that can be used on the overview pages of subpage sections. See
 * the various `Overview.tsx` files for examples.
 */
const usePartialSubmitForOverview = <T extends object>({
  value,
  onNext,
  shouldSubmit = true,
}: PartialSubmitForOverviewOptions<T>) => {
  const { showModal, closeModal } = useModal();
  const validate = useCallback(() => !!value?.items?.length, [value]);
  const onInvalid = useCallback(
    (doSubmit: () => void) => {
      showModal({
        message: "Are you sure you want to skip adding the next section?",
        actions: [
          { text: "Cancel", type: "inverse", action: closeModal },
          {
            text: "Okay",
            action: () => {
              closeModal();
              doSubmit();
            },
          },
        ],
      });
    },
    [showModal, closeModal]
  );
  return useMaybeSubmit({ onNext, validate, onInvalid, disableNextIfInvalid: false, shouldSubmit });
};

export default usePartialSubmitForOverview;
